// The campaign header's third line: what the status chip beside the name
// and the "checked" item in the meta line cannot say — a pause reason, a
// repair in progress, an attention sentence with the server's reason and a
// link to the publish log, the retry beside a blocked repair. Nothing else,
// so a plainly delivering campaign has no third line at all (Gio, 2026-09-18,
// canvas board 14). D1 evidence only: rendering it never contacts a provider.
import type { MouseEvent } from 'react';
import type { CampaignOperationalStatus } from '@/api/types';
import type { FailureView } from '@/api/types.gen';
import { CampaignRecoveryAction } from '@/components/campaign-recovery-action';
import { FailureNotice } from '@/components/failure-notice';
import { campaignStatusLine, type StatusLineProvider } from '@/lib/campaign-status-line';
import type { FlightExtensionSignal } from '@/lib/flight-extension';

export function CampaignStatusLine({
  status,
  provider,
  campaignId,
  onOpenPublishLog,
  failure,
  extension,
  onOpenExtension,
}: {
  status?: CampaignOperationalStatus | null;
  provider: StatusLineProvider;
  campaignId: string;
  /**
   * The failed provider sync in words (`shared::failure_copy`), when the
   * reason is that failure. The raw reason then sits under its "Technical
   * details" instead of being the line itself (2026-10-08: Wicked's header
   * read "upstream provider=meta operation=campaign_readback …").
   */
  failure?: FailureView | null;
  /** Opens and scrolls the publish log; the link's href is its fallback. */
  onOpenPublishLog?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** The newest Extend-flight request, when it has something to say (2026-10-21). */
  extension?: FlightExtensionSignal | null;
  /** Opens the Extend-flight dialog on that request. */
  onOpenExtension?: () => void;
}) {
  const line = campaignStatusLine(status, provider, Date.now(), extension);
  if (!line) return null;
  if (!line.attention && !line.note && !line.recoveryBlocked) return null;
  return (
    <div
      className={`campaign-status-line${line.attention ? ' is-attention' : ''}`}
      role="status"
      aria-label="Campaign delivery status"
    >
      {line.attention && <strong>{line.headline}</strong>}
      {line.note && <span className="campaign-status-line-note">{line.note}</span>}
      {line.reason && (failure
        ? <FailureNotice failure={failure} fallback={line.reason} dense announce={false} />
        : <span className="campaign-status-line-reason">{line.reason}</span>)}
      {line.attention && (
        <a href="#publish-log" onClick={onOpenPublishLog}>
          see the publish log
        </a>
      )}
      {line.recoveryBlocked && <CampaignRecoveryAction campaignId={campaignId} status={status} />}
      {line.extension && onOpenExtension && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={onOpenExtension}>
          Review the extension
        </button>
      )}
    </div>
  );
}
